/**
 * Mapping utilities for converting Mews API responses to local data structures
 * 
 * Phase 2: Data Mapping & Translation - Implementation Complete
 * 
 * This module provides bidirectional mappings between:
 * - Local room IDs ↔ Mews RoomCategoryId
 * - Adults/Children count ↔ Mews OccupancyData format
 * - Mews Products ↔ Local Upsells
 * - Mews Rate Plans ↔ Local Rate Plans
 */

import type {
  HotelsResponse,
  RoomCategory,
  AgeCategory,
  Product,
} from './types';
import {
  type Upsell,
  UPSELLS,
  collectSignatureProductIdsForUpsellMapping,
} from '@/data/upsells';
import {
  getMewsRoomCategoryId,
  getLocalRoomId,
  autoMatchRooms,
  setRoomMapping,
  getRoomMapping,
} from './room-mapping';
import {
  getMewsAgeCategoryId,
  getAgeCategoryMapping,
} from './age-category-mapping';

/**
 * Local rate plan ID type
 */
export type RatePlanId = 'standard' | 'signature';

/**
 * Maps Mews room category to local room structure
 * 
 * @param mewsRoomCategory - Room category from Mews API
 * @returns Local room structure (placeholder for future use)
 * 
 * @example
 * ```typescript
 * const localRoom = mapRoomCategory(mewsRoomCategory);
 * ```
 */
export function mapRoomCategory(mewsRoomCategory: RoomCategory): RoomCategory {
  // This is a placeholder - full mapping will be implemented when needed
  return mewsRoomCategory;
}

/**
 * Maps Mews age categories to local age category structure
 * 
 * @param mewsAgeCategories - Age categories from Configuration API
 * @returns Local age category structure (placeholder for future use)
 * 
 * @example
 * ```typescript
 * const localAgeCategories = mapAgeCategories(configuration.AgeCategories);
 * ```
 */
export function mapAgeCategories(mewsAgeCategories: AgeCategory[]): AgeCategory[] {
  // This is a placeholder - full mapping will be implemented when needed
  return mewsAgeCategories;
}

/**
 * Converts adults/children count to Mews OccupancyData format
 * 
 * The Mews API uses OccupancyData array with AgeCategoryId and PersonCount
 * for availability and pricing endpoints (not AdultCount/ChildCount).
 * 
 * @param adults - Number of adults (must be > 0)
 * @param children - Number of children (can be 0)
 * @param ageCategories - Age categories from Configuration API
 * @returns Mews OccupancyData array with AgeCategoryId and PersonCount
 * 
 * @example
 * ```typescript
 * const occupancies = convertToOccupancies(2, 1, ageCategories);
 * // Returns: [
 * //   { AgeCategoryId: "adult-id", PersonCount: 2 },
 * //   { AgeCategoryId: "child-id", PersonCount: 1 }
 * // ]
 * ```
 */
export function convertToOccupancies(
  adults: number,
  children: number,
  ageCategories: AgeCategory[]
): Array<{ AgeCategoryId: string; PersonCount: number }> {
  if (adults <= 0) {
    if (import.meta.env.DEV) {
      console.warn('[Occupancy] At least one adult is required');
    }
    return [];
  }

  // Helper function to extract localized name (defined at top level for reuse)
  const getName = (name: string | Record<string, string>, preferredLanguages: string[] = ['en-US', 'de-CH', 'de-DE', 'en-GB']): string => {
    if (typeof name === 'string') {
      return name;
    }
    // Try preferred languages first, then any available language
    for (const lang of preferredLanguages) {
      if (name[lang]) {
        return name[lang];
      }
    }
    // Fallback to first available language
    const firstKey = Object.keys(name)[0];
    return firstKey ? name[firstKey] : '';
  };

  // Log all age categories for debugging
  if (import.meta.env.DEV) {
    console.log('[Occupancy] Available age categories from Mews API:', ageCategories.map(cat => ({
      id: cat.Id.substring(0, 8) + '...',
      name: getName(cat.Name),
      localizedNames: typeof cat.Name === 'object' ? cat.Name : undefined,
      minAge: cat.MinimumAge,
      maxAge: cat.MaximumAge,
      fullId: cat.Id,
    })));
    console.log('[Occupancy] Converting:', { adults, children });
  }

  const result: Array<{ AgeCategoryId: string; PersonCount: number }> = [];

  // Try manual mapping first (fastest - O(1) lookup)
  const manualAdultId = getMewsAgeCategoryId('adult');
  let adultCategoryId: string | undefined = manualAdultId;

  // If manual mapping not found, fall back to auto-matching
  if (!adultCategoryId) {
    if (import.meta.env.DEV) {
      console.log('[Occupancy] No manual mapping for adult category, using auto-matching...');
    }

    // Find adult age category using name/age-based detection
    const adultCategory = ageCategories.find(category => {
      const name = getName(category.Name);
      const nameLower = name.toLowerCase();
      
      return (
        // English keywords
        nameLower.includes('adult') ||
        // German keywords
        nameLower.includes('erwachsener') ||
        nameLower.includes('erwachsene') ||
        // Age-based detection (most reliable)
        (category.MinimumAge !== undefined && category.MinimumAge >= 18) ||
        // Default to adult if no age specified (likely an adult category)
        (category.MaximumAge === undefined && category.MinimumAge === undefined)
      );
    });

    if (adultCategory) {
      adultCategoryId = adultCategory.Id;
    } else {
      if (import.meta.env.DEV) {
        console.warn('[Occupancy] Could not find adult age category. Using first category as fallback.');
      }
      // Fallback: use first category if no adult category found
      if (ageCategories.length > 0) {
        adultCategoryId = ageCategories[0].Id;
      }
    }
  } else if (import.meta.env.DEV) {
    console.log(`[Occupancy] Using manual mapping for adult: ${adultCategoryId.substring(0, 8)}...`);
  }

  if (adultCategoryId) {
    result.push({
      AgeCategoryId: adultCategoryId,
      PersonCount: adults,
    });
  }

  // Handle children
  if (children > 0) {
    // Try manual mapping first (fastest - O(1) lookup)
    const manualChildId = getMewsAgeCategoryId('child');
    let childCategoryId: string | undefined = manualChildId;

    // If manual mapping not found, fall back to auto-matching
    if (!childCategoryId) {
      if (import.meta.env.DEV) {
        console.log('[Occupancy] No manual mapping for child category, using auto-matching...');
      }

      // Find child age categories (not adult)
      // Strategy: Find categories that are clearly not adults
      const childCategories = ageCategories.filter(category => {
        // Skip the adult category we already found
        if (category.Id === adultCategoryId) return false;
      
      const name = getName(category.Name);
      const nameLower = name.toLowerCase();
      
      // Check for explicit child/infant/baby keywords (English and German)
      const hasChildKeyword = (
        // English keywords
        nameLower.includes('child') ||
        nameLower.includes('infant') ||
        nameLower.includes('baby') ||
        nameLower.includes('minor') ||
        nameLower.includes('youth') ||
        nameLower.includes('teen') ||
        // German keywords
        nameLower.includes('kind') ||
        nameLower.includes('kinder') ||
        nameLower.includes('säugling') ||
        nameLower.includes('kleinkind') || // "Kleinkinder" = toddlers/infants
        nameLower.includes('jugend') ||
        nameLower.includes('jugendlich')
      );
      
      // Check for age-based criteria (most reliable - works regardless of language)
      // A category is a child category if:
      // 1. It has a MaximumAge < 18, OR
      // 2. It has both Min and Max age, and Max < 18
      const isChildByAge = (
        (category.MaximumAge !== undefined && category.MaximumAge < 18) ||
        (category.MinimumAge !== undefined && category.MaximumAge !== undefined && 
         category.MaximumAge < 18 && category.MinimumAge < 18)
      );
      
      return hasChildKeyword || isChildByAge;
    });

      // If no explicit child categories found, try fallback: use any non-adult category
      let categoriesToUse = childCategories;
      if (categoriesToUse.length === 0) {
        if (import.meta.env.DEV) {
          console.log('[Occupancy] No explicit child categories found via keyword/age matching.');
          console.log('[Occupancy] Adult category ID:', adultCategoryId ? `${adultCategoryId.substring(0, 8)}...` : 'None');
          console.log('[Occupancy] All available age categories:', ageCategories.map(c => ({
            id: c.Id.substring(0, 8) + '...',
            name: getName(c.Name),
            minAge: c.MinimumAge,
            maxAge: c.MaximumAge,
            isAdult: c.Id === adultCategoryId,
          })));
          console.log('[Occupancy] Using fallback: any non-adult category.');
        }
        
        // Fallback: Use any category that's not the adult category
        categoriesToUse = ageCategories.filter(category => category.Id !== adultCategoryId);
        
        if (import.meta.env.DEV && categoriesToUse.length > 0) {
          console.log('[Occupancy] Fallback found', categoriesToUse.length, 'non-adult categories:', 
            categoriesToUse.map(c => `${getName(c.Name)} (Min: ${c.MinimumAge ?? 'N/A'}, Max: ${c.MaximumAge ?? 'N/A'})`));
        }
      }

      if (categoriesToUse.length === 0) {
        if (import.meta.env.DEV) {
          console.warn('[Occupancy] No child age categories found, but children > 0. Children will not be included in occupancy.');
        }
        // If still no categories, we can't add children - this is a data issue
      } else if (categoriesToUse.length === 1) {
        // Single child category - assign all children to it
        childCategoryId = categoriesToUse[0].Id;
        if (import.meta.env.DEV) {
          console.log(`[Occupancy] Assigning ${children} children to category: ${getName(categoriesToUse[0].Name)} (${categoriesToUse[0].Id.substring(0, 8)}...)`);
        }
      } else {
        // Multiple child categories - use the one with the lowest maximum age (youngest category)
        // or if no max age, use the first one
        const sortedCategories = [...categoriesToUse].sort((a, b) => {
          const aMax = a.MaximumAge ?? Infinity;
          const bMax = b.MaximumAge ?? Infinity;
          return aMax - bMax;
        });
        
        childCategoryId = sortedCategories[0].Id;
        if (import.meta.env.DEV) {
          console.log(
            `[Occupancy] Multiple child categories found (${categoriesToUse.length}). Assigning all ${children} children to youngest category: ${getName(sortedCategories[0].Name)} (MaxAge: ${sortedCategories[0].MaximumAge ?? 'N/A'})`
          );
        }
      }
    } else if (import.meta.env.DEV) {
      console.log(`[Occupancy] Using manual mapping for child: ${childCategoryId.substring(0, 8)}...`);
    }

    if (childCategoryId) {
      result.push({
        AgeCategoryId: childCategoryId,
        PersonCount: children,
      });
    }
  }

  return result;
}

/**
 * Maps Mews RateId to local RatePlanId
 * 
 * This is a helper function for rate plan mapping.
 * Rate plans from Mews are identified by RateId in availability/pricing responses.
 * 
 * @param rateId - Mews RateId
 * @param rateName - Optional rate name for matching
 * @returns Local RatePlanId ("standard" or "signature") or null if not found
 * 
 * @example
 * ```typescript
 * const ratePlanId = mapMewsRateToLocalRatePlan(rateId, rateName);
 * // Returns: "standard" | "signature" | null
 * ```
 */
export function mapMewsRateToLocalRatePlan(
  rateId: string,
  rateName?: string
): RatePlanId | null {
  // If env mapping is present, use it as the only source of truth (no fallbacks).
  const envMapping = import.meta.env.VITE_MEWS_RATE_MAPPING_JSON;
  if (envMapping) {
    try {
      const mapping = JSON.parse(envMapping) as Record<string, RatePlanId>;
      if (mapping[rateId]) {
        return mapping[rateId];
      }
      // Rate not in env mapping: return null. Do not use name fallback or default.
      if (import.meta.env.DEV) {
        const expectedLocalRates: RatePlanId[] = ['standard', 'signature'];
        const mappedLocalRates = [...new Set(Object.values(mapping).filter((v): v is RatePlanId => v === 'standard' || v === 'signature'))];
        const unmappedLocalRates = expectedLocalRates.filter((r) => !mappedLocalRates.includes(r));
        console.warn(
          '[Rate Plan] MEWS RateId not in VITE_MEWS_RATE_MAPPING_JSON; returning null.',
          unmappedLocalRates.length > 0
            ? `Local rate plan(s) not mapped in VITE_MEWS_RATE_MAPPING_JSON: ${unmappedLocalRates.join(', ')}.`
            : 'All local rate plans (standard, signature) have at least one MEWS RateId assigned.',
          { rateId },
        );
      }
      return null;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[Rate Plan] Failed to parse rate mapping from environment:', error);
      }
      // Parse failed: treat as no env mapping and continue to fallbacks below.
    }
  }

  // Fallbacks only when env mapping is absent (or invalid).
  // Match by rate name
  if (rateName) {
    const nameLower = rateName.toLowerCase();
    if (nameLower.includes('signature') || nameLower.includes('privilege')) {
      return 'signature';
    }
    if (nameLower.includes('standard') || nameLower.includes('daily')) {
      return 'standard';
    }
  }

  // Default to standard if no match found
  if (import.meta.env.DEV) {
    console.warn(
      `[Rate Plan] Could not map rate "${rateId}" to local rate plan. Defaulting to "standard".`,
    );
  }
  return 'standard';
}

/**
 * Maps Mews rate plan to local rate plan structure
 * 
 * This is a placeholder function that will be fully implemented in Phase 3
 * when we have actual rate plan data from availability/pricing responses.
 * 
 * @param mewsRatePlan - Rate plan from Mews API (Rate object from availability/pricing response)
 * @returns Local rate plan structure (placeholder for now)
 * 
 * @example
 * ```typescript
 * const localRatePlan = mapRatePlan(mewsRatePlan);
 * ```
 */
export function mapRatePlan(mewsRatePlan: unknown): unknown {
  // TODO: Full implementation in Phase 3 when we have Rate objects from API
  // For now, this is a placeholder
  return mewsRatePlan;
}

/**
 * Extracts selling price (GrossValue) from Mews Product.Pricing structure.
 * Prefers the given currency (e.g. CHF); MEWS Value is keyed by currency with GrossValue.
 *
 * @param product - Mews Product with Pricing information
 * @param currencyCode - ISO currency (default 'CHF') for Absolute pricing Value[currency].GrossValue
 * @returns Selling price or null if not determined
 */
export function extractProductPrice(
  product: Product,
  currencyCode: string = 'CHF'
): number | null {
  if (!product.Pricing) {
    return null;
  }

  try {
    const disc = String(product.Pricing.Discriminator ?? '').trim().toLowerCase();
    if (disc === 'absolute') {
      const value = product.Pricing.Value;
      if (typeof value === 'number') {
        return value;
      }
      if (typeof value === 'object' && value !== null) {
        const valueObj = value as Record<string, unknown>;
        // MEWS: Value is keyed by currency, e.g. Value.CHF.GrossValue
        const byCurrency = valueObj[currencyCode] as Record<string, unknown> | undefined;
        if (byCurrency && typeof byCurrency.GrossValue === 'number') {
          return byCurrency.GrossValue as number;
        }
        if (typeof valueObj.Amount === 'number') {
          return valueObj.Amount as number;
        }
        if (typeof valueObj.Value === 'number') {
          return valueObj.Value as number;
        }
      }
    } else if (disc === 'relative') {
      return null;
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn(`[Product Price] Failed to extract price for product "${product.Id}":`, error);
    }
  }

  return null;
}

/**
 * Builds a map of MEWS Product Id -> selling price (GrossValue in given currency).
 * Only Absolute-priced products are included; Relative products are skipped.
 */
export function buildProductPriceMap(
  products: Product[],
  currencyCode: string = 'CHF'
): Map<string, number> {
  const map = new Map<string, number>();
  for (const product of products) {
    const price = extractProductPrice(product, currencyCode);
    if (price != null) {
      map.set(product.Id, price);
    }
  }
  return map;
}

/**
 * Maps Mews CategoryId to local category string
 * 
 * Maps Mews product category IDs to local upsell categories:
 * "Dining", "Romance", "Spa & Massage", "Sauna & Thermal"
 * 
 * @param mewsCategoryId - Mews CategoryId
 * @param categoryName - Optional category name for matching
 * @returns Local category string or null if not found
 * 
 * @example
 * ```typescript
 * const category = mapMewsCategoryToLocal("cat-123", "Dining Services");
 * // Returns: "Dining"
 * ```
 */
export function mapMewsCategoryToLocal(
  mewsCategoryId: string,
  categoryName?: string
): Upsell['category'] | null {
  // Check for explicit mapping in environment variable
  const envMapping = import.meta.env.VITE_MEWS_CATEGORY_MAPPING_JSON;
  if (envMapping) {
    try {
      const mapping = JSON.parse(envMapping) as Record<string, Upsell['category']>;
      if (mapping[mewsCategoryId]) {
        return mapping[mewsCategoryId];
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[Category Mapping] Failed to parse category mapping from environment:', error);
      }
    }
  }

  // Fallback: match by category name
  if (categoryName) {
    const nameLower = categoryName.toLowerCase();
    if (nameLower.includes('dining') || nameLower.includes('restaurant') || nameLower.includes('food')) {
      return 'Dining';
    }
    if (nameLower.includes('sauna') || nameLower.includes('thermal')) {
      return 'Sauna & Thermal';
    }
    if (nameLower.includes('wellness') || nameLower.includes('spa') || nameLower.includes('massage')) {
      return 'Spa & Massage';
    }
    if (nameLower.includes('romance') || nameLower.includes('romantic') || nameLower.includes('decoration')) {
      return 'Romance';
    }
  }

  // Default to null if no match found
  if (import.meta.env.DEV) {
    console.warn(`[Category Mapping] Could not map category "${mewsCategoryId}" to local category`);
  }
  return null;
}

/**
 * Constructs image URL for a Mews product
 * 
 * @param imageId - Mews ImageId
 * @param imageBaseUrl - Image base URL from HotelsResponse
 * @returns Full image URL or placeholder if base URL not available
 * 
 * @example
 * ```typescript
 * const imageUrl = constructProductImageUrl(imageId, hotelsResponse.ImageBaseUrl);
 * ```
 */
export function constructProductImageUrl(
  imageId: string | undefined,
  imageBaseUrl: string | undefined
): string {
  if (!imageId || !imageBaseUrl) {
    return '/placeholder.svg'; // Fallback placeholder
  }

  // Construct full URL (format may vary - adjust based on actual Mews API format)
  return `${imageBaseUrl}/${imageId}`;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function productMappingObjectToFlat(
  obj: Record<string, unknown>
): Record<string, string> | null {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (typeof v !== 'string') return null;
    out[k] = v;
  }
  return out;
}

/**
 * Parses **flat** JSON: `{ "<MewsProductId>": "<localUpsellId>", ... }`.
 * Used for `VITE_MEWS_PRODUCT_MAPPING_SIGNATURE_JSON` and for the primary var when it is a flat map.
 */
export function parseMewsProductMappingFlatJson(raw: string): Record<string, string> | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isPlainObject(parsed)) return null;
    return productMappingObjectToFlat(parsed);
  } catch {
    return null;
  }
}

/**
 * Parses `VITE_MEWS_PRODUCT_MAPPING_JSON` (standard-rate products, plus legacy all-in-one).
 *
 * - **Flat:** `{ "<MewsProductId>": "<localUpsellId>", ... }`
 * - **Legacy single-var split:** `{ "standard": { ... }, "signature": { ... } }` — merged; inner `signature` wins on duplicate keys.
 *
 * Prefer **two env vars**: primary = standard (flat or legacy nested), plus
 * `VITE_MEWS_PRODUCT_MAPPING_SIGNATURE_JSON` merged on top (signature env wins on duplicate keys).
 */
export function parseMewsProductMappingEnvJson(raw: string): Record<string, string> | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isPlainObject(parsed)) return null;

    const std = parsed.standard;
    const sig = parsed.signature;
    if (isPlainObject(std) && isPlainObject(sig)) {
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(std)) {
        if (typeof v === 'string') out[k] = v;
      }
      for (const [k, v] of Object.entries(sig)) {
        if (typeof v === 'string') out[k] = v;
      }
      return out;
    }

    return productMappingObjectToFlat(parsed);
  } catch {
    return null;
  }
}

let mewsProductMappingCacheKey: string | undefined;
let mewsProductMappingCache: Record<string, string> | null | undefined;

/**
 * Merges `VITE_MEWS_PRODUCT_MAPPING_JSON` + signature ProductIds from `UPSELLS` (`mews*Signature` fields)
 * + optional `VITE_MEWS_PRODUCT_MAPPING_SIGNATURE_JSON` (env overrides on duplicate keys).
 */
export function getMergedMewsProductMappingLookup(): Record<string, string> | null {
  const mainRaw = import.meta.env.VITE_MEWS_PRODUCT_MAPPING_JSON ?? '';
  const sigRaw = import.meta.env.VITE_MEWS_PRODUCT_MAPPING_SIGNATURE_JSON ?? '';
  const fromCode = collectSignatureProductIdsForUpsellMapping(UPSELLS);
  const codeFingerprint = Object.keys(fromCode)
    .sort()
    .map((k) => `${k}:${fromCode[k]}`)
    .join('\n');
  const cacheKey = `${mainRaw}\0${sigRaw}\0${codeFingerprint}`;
  if (mewsProductMappingCacheKey === cacheKey && mewsProductMappingCache !== undefined) {
    return mewsProductMappingCache;
  }
  mewsProductMappingCacheKey = cacheKey;

  let primary: Record<string, string> = {};
  if (mainRaw) {
    const parsed = parseMewsProductMappingEnvJson(mainRaw);
    if (parsed === null && import.meta.env.DEV) {
      console.warn('[Service Mapping] Failed to parse VITE_MEWS_PRODUCT_MAPPING_JSON');
    } else if (parsed) {
      primary = parsed;
    }
  }

  let merged: Record<string, string> = { ...primary, ...fromCode };

  if (sigRaw) {
    const overlay = parseMewsProductMappingFlatJson(sigRaw);
    if (overlay) {
      merged = { ...merged, ...overlay };
    } else if (import.meta.env.DEV) {
      console.warn(
        '[Service Mapping] Failed to parse VITE_MEWS_PRODUCT_MAPPING_SIGNATURE_JSON (expect flat { "ProductId": "localUpsellId" })',
      );
    }
  }

  if (Object.keys(merged).length === 0) {
    mewsProductMappingCache = null;
    return null;
  }
  mewsProductMappingCache = merged;
  return mewsProductMappingCache;
}

/**
 * Maps Mews Product to local Upsell structure
 * 
 * @param mewsProduct - Product from Mews API (from HotelsResponse.Products)
 * @param languageCode - Language code for localized strings (default: "en-US")
 * @param imageBaseUrl - Image base URL from HotelsResponse
 * @returns Local Upsell object or null if mapping fails
 * 
 * @example
 * ```typescript
 * const upsell = mapService(product, "en-US", hotelsResponse.ImageBaseUrl);
 * ```
 */
export function mapService(
  mewsProduct: Product,
  languageCode: string = 'en-US',
  imageBaseUrl?: string
): Upsell | null {
  try {
    // Extract localized name
    const name = mewsProduct.Name[languageCode] || Object.values(mewsProduct.Name)[0] || 'Unknown Product';
    
    // Extract localized description
    const description = mewsProduct.Description
      ? (mewsProduct.Description[languageCode] || Object.values(mewsProduct.Description)[0] || '')
      : '';

    // Extract price
    const price = extractProductPrice(mewsProduct);
    if (price === null) {
      // Skip products without absolute pricing for now
      if (import.meta.env.DEV) {
        console.log(`[Service Mapping] Skipping product "${name}" - no absolute price available`);
      }
      return null;
    }

    // Map category
    const category = mapMewsCategoryToLocal(mewsProduct.CategoryId);
    if (!category) {
      // Skip products without valid category mapping
      if (import.meta.env.DEV) {
        console.log(`[Service Mapping] Skipping product "${name}" - no category mapping`);
      }
      return null;
    }

    // Use local upsell id when product mapping is configured (Mews ProductId → local id)
    let id = mewsProduct.Id;
    const mapping = getMergedMewsProductMappingLookup();
    if (mapping?.[mewsProduct.Id]) {
      id = mapping[mewsProduct.Id]!;
    }

    // Construct image URL
    const image = constructProductImageUrl(mewsProduct.ImageId, imageBaseUrl);

    return {
      id,
      name,
      description,
      price,
      image,
      category,
    };
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn(`[Service Mapping] Failed to map product "${mewsProduct.Id}":`, error);
    }
    return null;
  }
}

/**
 * Maps array of Mews Products to array of local Upsells
 * 
 * Filters out products that don't map cleanly (missing price, category, etc.)
 * 
 * @param products - Array of Mews Products
 * @param languageCode - Language code for localized strings (default: "en-US")
 * @param imageBaseUrl - Image base URL from HotelsResponse
 * @returns Array of local Upsell objects
 * 
 * @example
 * ```typescript
 * const upsells = mapProductsToUpsells(products, "en-US", hotelsResponse.ImageBaseUrl);
 * ```
 */
export function mapProductsToUpsells(
  products: Product[],
  languageCode: string = 'en-US',
  imageBaseUrl?: string
): Upsell[] {
  const upsells: Upsell[] = [];

  for (const product of products) {
    // Skip products included by default (they're not upsells)
    if (product.IncludedByDefault) {
      continue;
    }

    const upsell = mapService(product, languageCode, imageBaseUrl);
    if (upsell) {
      upsells.push(upsell);
    }
  }

  return upsells;
}

/**
 * Maps local room ID to Mews RoomCategoryId
 * 
 * Uses room mapping configuration from room-mapping.ts.
 * If mapping doesn't exist, attempts auto-matching by name similarity.
 * 
 * @param localRoomId - Local room identifier (sourceRoom.id from ROOM_LADDER)
 * @param hotelsResponse - Hotels response containing room categories
 * @returns Mews RoomCategoryId or undefined if not found
 * 
 * @example
 * ```typescript
 * const roomCategoryId = mapLocalRoomToMews("junior-suite", hotelsResponse);
 * ```
 */
export function mapLocalRoomToMews(
  localRoomId: string,
  hotelsResponse: HotelsResponse
): string | undefined {
  // First, try to get from existing mapping
  let mewsRoomCategoryId = getMewsRoomCategoryId(localRoomId);

  if (mewsRoomCategoryId) {
    return mewsRoomCategoryId;
  }

  // If not found, attempt auto-matching
  if (import.meta.env.DEV) {
    console.log(`[Room Mapping] No explicit mapping for "${localRoomId}", attempting auto-match...`);
  }

  const languageCode = hotelsResponse.DefaultLanguageCode || 'en-US';
  const autoMapping = autoMatchRooms(
    [localRoomId],
    hotelsResponse.RoomCategories,
    languageCode
  );

  if (autoMapping[localRoomId]) {
    // Store the auto-matched mapping for future use
    const currentMapping = getRoomMapping();
    setRoomMapping({ ...currentMapping, ...autoMapping });
    return autoMapping[localRoomId];
  }

  if (import.meta.env.DEV) {
    console.warn(`[Room Mapping] Could not map local room "${localRoomId}" to Mews RoomCategoryId`);
  }

  return undefined;
}

/**
 * Maps Mews RoomCategoryId to local room ID
 * 
 * Reverse lookup: Given a Mews RoomCategoryId, find the corresponding local sourceRoom.id.
 * 
 * @param mewsRoomCategoryId - Mews room category identifier
 * @param hotelsResponse - Hotels response containing room categories
 * @returns Local room ID or undefined if not found
 * 
 * @example
 * ```typescript
 * const localRoomId = mapMewsToLocalRoom(mewsRoomCategoryId, hotelsResponse);
 * ```
 */
export function mapMewsToLocalRoom(
  mewsRoomCategoryId: string,
  hotelsResponse: HotelsResponse
): string | undefined {
  // First, try to get from existing mapping
  const localRoomId = getLocalRoomId(mewsRoomCategoryId);
  if (localRoomId) {
    return localRoomId;
  }

  // If not found, try to find the Mews room category and attempt reverse matching
  const mewsRoomCategory = hotelsResponse.RoomCategories.find(
    room => room.Id === mewsRoomCategoryId
  );

  if (!mewsRoomCategory) {
    if (import.meta.env.DEV) {
      console.warn(`[Room Mapping] Mews RoomCategoryId "${mewsRoomCategoryId}" not found in hotels response`);
    }
    return undefined;
  }

  // For reverse matching, we would need a list of all local room IDs
  // This is a limitation - we can't do reverse auto-matching without knowing all local IDs
  // In practice, the mapping should be configured explicitly
  if (import.meta.env.DEV) {
    console.warn(
      `[Room Mapping] No reverse mapping found for Mews RoomCategoryId "${mewsRoomCategoryId}". ` +
      `Consider configuring explicit mappings.`
    );
  }

  return undefined;
}
